import { useState, useMemo } from 'react';
import Layout from '../../components/layout/Layout';
import LessonHistoryItem from '../../components/history/LessonHistoryItem';
import FilterSidebar from '../../components/history/FilterSidebar';
import { LessonHistoryFilters } from '../../types/history';
import { useLessonHistory } from '../../hooks/useLessonHistory';

export default function LessonHistoryPage() {
  const [filters, setFilters] = useState<LessonHistoryFilters>({});
  const { lessons, loading, error, refetch } = useLessonHistory();

  // Extract unique subjects for filters
  const subjects = useMemo(() => {
    return Array.from(new Set(lessons.map((lesson) => lesson.subject)));
  }, [lessons]);

  // Apply filters
  const filteredLessons = useMemo(() => {
    return lessons.filter((lesson) => {
      // Subject filter
      if (filters.subject && lesson.subject !== filters.subject) return false;
      
      // Search text filter - search in summary, subject, course, and topic
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const searchableText = [
          lesson.summary,
          lesson.subject,
          lesson.course,
          lesson.topic,
        ].filter(Boolean).join(' ').toLowerCase();
        
        if (!searchableText.includes(searchLower)) return false;
      }
      
      return true;
    });
  }, [lessons, filters]);

  if (loading) {
    return (
      <Layout>
        <div className="gap-1 px-6 flex flex-1 justify-center py-5">
          <div className="layout-content-container flex flex-col max-w-[920px] flex-1">
            <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 text-left pb-3 pt-5">
              Lesson History
            </h2>
            <div className="flex items-center justify-center py-8">
              <div className="text-text-secondary text-base">Loading lesson history...</div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="gap-1 px-6 flex flex-1 justify-center py-5">
          <div className="layout-content-container flex flex-col max-w-[920px] flex-1">
            <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 text-left pb-3 pt-5">
              Lesson History
            </h2>
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div className="text-red-400 text-base">Failed to load lesson history: {error}</div>
              <button
                onClick={refetch}
                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded h-10 px-4 bg-border text-white text-sm font-medium leading-normal"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="gap-1 px-6 flex flex-1 justify-center py-5">
        <div className="layout-content-container flex flex-col max-w-[920px] flex-1">
          <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 text-left pb-3 pt-5">
            Lesson History
          </h2>

          {filteredLessons.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-text-secondary text-base">
                {lessons.length === 0 
                  ? "No lessons found. Start your first lesson to see it here!"
                  : "No lessons match your current filters."
                }
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredLessons.map((lesson) => (
                <LessonHistoryItem key={lesson.id} lesson={lesson} />
              ))}
            </div>
          )}
        </div>

        <FilterSidebar
          filters={filters}
          onFiltersChange={setFilters}
          subjects={subjects}
        />
      </div>
    </Layout>
  );
}
